import { Board } from "../board";
import { DestroyableObstacle } from "../destroyableObstacle";
import { DistanceMap } from "../distanceMap";
import { Direction } from "../direction";
import { Mob } from "../mob";
import { FloatingRobot } from "../mobs/floatingRobot";
import { PlowRobot } from "../mobs/plowRobot";
import { Robot } from "../mobs/robot";
import type { PhysicalThing } from "../physicalThing";
import { randomChance, randomElement, randomInt, weightedRandom } from "../random";
import { Strings } from "../strings";
import type { Thing } from "../thing";
import { BlastDoor } from "../things/blastDoor";
import { ConcreteBlock } from "../things/concreteBlock";
import { Downstairs } from "../things/downstairs";
import { Equation } from "../things/equation";
import { GraySlab } from "../things/floors/graySlab";
import { GrayStoneFloor1 } from "../things/floors/grayStoneFloor1";
import { InvisibleWall } from "../things/invisibleWall";
import { LevelChangerThing } from "../things/levelChangerThing";
import { LootBox } from "../things/lootBox";
import { MetalBlock } from "../things/metalBlock";
import { MetalDoor } from "../things/metalDoor";
import { OpenBlastDoorsTerminal } from "../things/openBlastDoorsTerminal";
import { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import { PottedPlant } from "../things/pottedPlant";
import { RobotSpawner } from "../things/robotSpawner";
import { SlidingDoor } from "../things/slidingDoor";
import { Upstairs } from "../things/upstairs";
import { WoodenDoor } from "../things/woodenDoor";
import { XY } from "../xy";
import { generateWithRetries, type LevelAcceptanceCriteria } from "./levelGenerator";
import { LevelGeneratorScrewUp } from "./levelGeneratorScrewUp";
import {
	breadthFirstSearch,
	getSingleThingPrefab,
	populateLootBoxes,
	populateVendingMachines,
	putPrefabOnArea,
	removeAllThingsAt,
	removeExcessiveThings,
	assignGoldToPrePlacedMobs,
} from "./levelGeneratorUtils";
import { LootTable } from "./lootTable";
import type { VendingTable } from "./vendingTable";
import { Prefab } from "./prefab";
import { getBigContainerPrefab } from "./prefabs/generalPrefabs";
import {
	getDesksPrefab,
	getPlantsCornerPrefab,
	getDeskPrefab,
	getWoodenDesksPrefab,
	getShelvesPrefab,
	getMachinesWithWarningStripesPrefab,
	getComputerDesksPrefab,
	getSingleComputerDeskPrefab,
	getMetalCornerPrefab,
	getBorderPrefab,
	getFenceBoxesPrefab,
	getPillarsPrefab,
	getFencedLootBoxesPrefab,
	getOfflineRobots,
	getOfflineRobots2,
	getCornerPrisonCellPrefab,
	getBarrelsOnWallPrefab,
	getExplosiveBarrelsOnWallPrefab,
	getExplosiveBarrelsPrefab,
	getFenceWithPlants,
} from "./prefabs/robotBasePrefabs";
import { ALL_ROOM_TYPES, RobotBaseRoom, RoomType } from "./robotBaseRoom";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";
import { buildPrefab } from "./prefabs/prefabBuilder.ts";
import {
	borderData,
	machinesAtBorderData,
	machinesDoubleRowAtBorderData,
	offlineRobots3Data,
	offlineRobotsData,
} from "./prefabs/robotBasePrefabData.ts";
import { EngineerAI } from "../ai/engineerAI.ts";
import { EngineerRobot } from "../mobs/engineerRobot.ts";

export interface RequiredPrefab {
	prefab: Prefab;
	allowedRoomTypes?: RoomType[];
	canBePlacedInVaultRoom?: boolean;
	canBePlacedInStarterRoom?: boolean;
}

export interface RobotBaseLevelParams {
	levelNumber: number;
	rng?: () => number;

	// BSP params
	minRoomSize?: number;
	maxRoomSize?: number;
	roomProbability?: number;
	corridorRoomProbability?: number;
	openDoorProbability?: number;
	maxOpeningRatio?: number;
	vaultRoomProbability?: number;
	lockedExitProbability?: number;
	requiredDecorationPercentMin?: number;
	requiredDecorationPercentMax?: number;
	margin?: number;

	wallSupplier?: () => PhysicalThing;

	// Population
	rewardLevel?: number;
	placeEquation?: boolean;
	placeExit?: boolean;
	exitLevelNumber?: number | null;
	previousLevelNumber?: number | null;
	entranceSupplier?: (xy: XY, destinationLevel: number) => LevelChangerThing;
	exitSupplier?: (xy: XY, destinationLevel: number) => LevelChangerThing;
	numberOfSpawners?: number;
	maxMobs?: number;
	maxLootBoxes?: number;
	minExitDistance?: number;
	lootTable?: LootTable;
	metalLootTable?: LootTable;
	vendingTable?: VendingTable | null;
	availableRoomTypes?: RoomType[];
	requiredPrefabs?: RequiredPrefab[];

	accept?: LevelAcceptanceCriteria;
}

export const EXIT_ROOM_DOOR_ID = "exitRoom";

const DEFAULTS: Required<Omit<RobotBaseLevelParams, "levelNumber" | "accept">> = {
	rng: Math.random,
	minRoomSize: 6,
	maxRoomSize: 12,
	roomProbability: 0.5,
	corridorRoomProbability: 0.3,
	openDoorProbability: 0.1,
	maxOpeningRatio: 0.18,
	vaultRoomProbability: 0.3,
	lockedExitProbability: 1,
	requiredDecorationPercentMin: 0.2,
	requiredDecorationPercentMax: 0.7,
	margin: 1,
	wallSupplier: () => new ConcreteBlock(new XY(0, 0)),
	rewardLevel: 0,
	placeEquation: false,
	placeExit: true,
	exitLevelNumber: null,
	previousLevelNumber: null,
	entranceSupplier: (xy, lvl) => new Upstairs(xy, lvl),
	exitSupplier: (xy, lvl) => new Downstairs(xy, lvl),
	numberOfSpawners: 4,
	maxMobs: 5,
	maxLootBoxes: 10,
	minExitDistance: 10,
	lootTable: new LootTable(),
	metalLootTable: new LootTable(),
	vendingTable: null,
	availableRoomTypes: [...ALL_ROOM_TYPES],
	requiredPrefabs: [],
};

type ResolvedParams = typeof DEFAULTS & { levelNumber: number };

export function generateRobotBaseLevel(params: RobotBaseLevelParams): Board {
	const p: ResolvedParams = { ...DEFAULTS, ...params };
	return generateWithRetries(p.levelNumber, () => new RobotBaseGeneration(p).runGeneration(), params.accept);
}

// The BSP pass carries a lot of interlocking state (rooms, door tiles, path
// caches), so it stays a class internally; a fresh instance runs per attempt.
class RobotBaseGeneration {
	board!: Board;
	rooms: Set<RobotBaseRoom> = new Set();
	vaultRooms: Set<RobotBaseRoom> = new Set();
	doorTiles: Set<string> = new Set();
	tilesReservedForPath: Set<string> = new Set();
	reservedPrefabTiles: Set<string> = new Set();
	protectedThings: Set<Thing> = new Set();
	private doorToDoorPathCache: Map<RobotBaseRoom, Set<string>> = new Map();
	private generationPlayerXY: XY | null = null;

	constructor(private readonly p: ResolvedParams) {}

	// Helper methods

	putWall(thing: PhysicalThing, xy: XY): void {
		if (this.doorTiles.has(xy.toString())) return;
		const things = this.board.getThingsAt(xy);
		if (things.some((t) => t.active && t.blocksMovement)) return;
		thing.xy = xy;
		this.board.addThing(thing);
	}

	findRoom(xy: XY): RobotBaseRoom | null {
		for (const room of this.rooms) {
			if (room.containsInside(xy)) return room;
		}
		return null;
	}

	isClear(xy: XY): boolean {
		return this.board.isPassable(xy) && !this.board.hasMob(xy);
	}

	fillRoom(room: RobotBaseRoom, supplier: () => PhysicalThing): void {
		for (const xy of room.getInnerArea()) {
			const thing = supplier();
			thing.xy = xy;
			this.board.addThing(thing);
		}
	}

	getRoomDoors(room: RobotBaseRoom): XY[] {
		const allTiles = room.getAllTiles();
		const result: XY[] = [];
		for (const xy of allTiles) {
			if (this.doorTiles.has(xy.toString())) {
				result.push(xy);
			}
		}
		return result;
	}

	hasAdjacentDoor(xy: XY): boolean {
		for (const neighbor of xy.getCardinalNeighbors()) {
			if (this.doorTiles.has(neighbor.toString())) return true;
		}
		return false;
	}

	isDoorTile(xy: XY): boolean {
		return this.doorTiles.has(xy.toString());
	}

	partitionRoom(room: RobotBaseRoom): void {
		// Viable X cuts (for vertical splits)
		const viableXCuts: number[] = [];
		for (let x = room.minXY.x + this.p.minRoomSize; x <= room.maxXY.x - this.p.minRoomSize; x++) {
			if (
				!this.doorTiles.has(new XY(x, room.minXY.y).toString()) &&
				!this.doorTiles.has(new XY(x, room.maxXY.y).toString())
			) {
				viableXCuts.push(x);
			}
		}

		// Viable Y cuts (for horizontal splits)
		const viableYCuts: number[] = [];
		for (let y = room.minXY.y + this.p.minRoomSize; y <= room.maxXY.y - this.p.minRoomSize; y++) {
			if (
				!this.doorTiles.has(new XY(room.minXY.x, y).toString()) &&
				!this.doorTiles.has(new XY(room.maxXY.x, y).toString())
			) {
				viableYCuts.push(y);
			}
		}

		const canSplitVertically = viableXCuts.length > 0;
		const canSplitHorizontally = viableYCuts.length > 0;

		// Accept room if within desired size range and probability check passes
		const w = room.getWidth();
		const h = room.getHeight();
		if (
			w >= this.p.minRoomSize &&
			w <= this.p.maxRoomSize &&
			h >= this.p.minRoomSize &&
			h <= this.p.maxRoomSize &&
			randomChance(this.p.roomProbability, this.p.rng)
		) {
			this.rooms.add(room);
			return;
		}

		// Determine split direction
		let splitHorizontally: boolean;
		if (canSplitHorizontally && canSplitVertically) {
			splitHorizontally = randomChance(0.5, this.p.rng);
		} else if (canSplitHorizontally) {
			splitHorizontally = true;
		} else if (canSplitVertically) {
			splitHorizontally = false;
		} else {
			this.rooms.add(room);
			return;
		}

		let room1: RobotBaseRoom;
		let room2: RobotBaseRoom;
		let segment: XY[];

		if (splitHorizontally) {
			const cutY = randomElement(viableYCuts, this.p.rng)!;
			room1 = new RobotBaseRoom(room.minXY, new XY(room.maxXY.x, cutY));
			room2 = new RobotBaseRoom(new XY(room.minXY.x, cutY), room.maxXY);
			segment = [];
			for (let x = room.minXY.x + 1; x <= room.maxXY.x - 1; x++) {
				segment.push(new XY(x, cutY));
			}
		} else {
			const cutX = randomElement(viableXCuts, this.p.rng)!;
			room1 = new RobotBaseRoom(room.minXY, new XY(cutX, room.maxXY.y));
			room2 = new RobotBaseRoom(new XY(cutX, room.minXY.y), room.maxXY);
			segment = [];
			for (let y = room.minXY.y + 1; y <= room.maxXY.y - 1; y++) {
				segment.push(new XY(cutX, y));
			}
		}

		this.getOpeningPositionsForSegment(segment);
		this.partitionRoom(room1);
		this.partitionRoom(room2);
	}

	getOpeningPositionsForSegment(segment: XY[]): XY[] {
		const maxOpenings = Math.max(1, Math.ceil(segment.length * this.p.maxOpeningRatio));
		const numberOfOpenings = randomInt(1, maxOpenings, this.p.rng);

		// Pick door indices; allow pairs but never 3 consecutive doors.
		const picked = new Set<number>();
		const candidates: number[] = segment.map((_, i) => i);

		const wouldFormTriple = (i: number): boolean =>
			(picked.has(i - 2) && picked.has(i - 1)) ||
			(picked.has(i - 1) && picked.has(i + 1)) ||
			(picked.has(i + 1) && picked.has(i + 2));

		while (picked.size < numberOfOpenings && candidates.length > 0) {
			const k = Math.floor(this.p.rng() * candidates.length);
			const idx = candidates[k];
			candidates[k] = candidates[candidates.length - 1];
			candidates.pop();
			picked.add(idx);

			for (let j = candidates.length - 1; j >= 0; j--) {
				if (wouldFormTriple(candidates[j])) {
					candidates[j] = candidates[candidates.length - 1];
					candidates.pop();
				}
			}
		}

		const positions: XY[] = [...picked].map((i) => segment[i]);

		for (const pos of positions) {
			const door = new WoodenDoor(pos);
			if (randomChance(this.p.openDoorProbability, this.p.rng)) {
				door.bump(Direction.N, door);
			}
			this.board.addThing(door);
			this.doorTiles.add(pos.toString());
		}

		return positions;
	}

	// --- Corridor methods ---

	get2X2Brush(center: XY): XY[] {
		return [
			center,
			center.addDirection(Direction.N),
			center.addDirection(Direction.W),
			center.addDirection(Direction.NW),
		];
	}

	get3X3Brush(center: XY): XY[] {
		return [center, ...center.getNeighbors()];
	}

	getRandomWalkPath(origin: XY, destination: XY): Set<string> {
		const deltaX = destination.x - origin.x;
		const deltaY = destination.y - origin.y;
		const path = new Set<string>();
		let current = origin;
		path.add(current.toString());
		while (!current.equals(destination)) {
			let horizontal: boolean;
			if (current.x === destination.x) {
				horizontal = false;
			} else if (current.y === destination.y) {
				horizontal = true;
			} else {
				horizontal = randomChance(0.5, this.p.rng);
			}
			if (horizontal) {
				current = current.add(Math.sign(deltaX), 0);
			} else {
				current = current.add(0, Math.sign(deltaY));
			}
			path.add(current.toString());
		}
		return path;
	}

	getDoorToDoorPaths(room: RobotBaseRoom): Set<string> {
		const cached = this.doorToDoorPathCache.get(room);
		if (cached) return cached;

		const doors = this.getRoomDoors(room);
		const entrances: XY[] = doors.map((doorXY) => {
			if (doorXY.y === room.maxXY.y) return doorXY.addDirection(Direction.N);
			if (doorXY.y === room.minXY.y) return doorXY.addDirection(Direction.S);
			if (doorXY.x === room.minXY.x) return doorXY.addDirection(Direction.E);
			if (doorXY.x === room.maxXY.x) return doorXY.addDirection(Direction.W);
			return doorXY;
		});

		if (entrances.length === 1) {
			entrances.push(room.getCenterXY());
		}

		let workingDoor = entrances[0];
		const unlinked = entrances.slice(1);
		const allPathKeys = new Set<string>();

		while (unlinked.length > 0) {
			let closestIdx = 0;
			let closestDist = workingDoor.distanceSquared(unlinked[0]);
			for (let i = 1; i < unlinked.length; i++) {
				const d = workingDoor.distanceSquared(unlinked[i]);
				if (d < closestDist) {
					closestDist = d;
					closestIdx = i;
				}
			}
			const closestDoor = unlinked[closestIdx];
			const pathKeys = this.getRandomWalkPath(workingDoor, closestDoor);
			for (const key of pathKeys) allPathKeys.add(key);
			workingDoor = closestDoor;
			unlinked.splice(closestIdx, 1);
		}

		this.doorToDoorPathCache.set(room, allPathKeys);
		return allPathKeys;
	}

	fillRoomWithCorridors(room: RobotBaseRoom): void {
		room.corridor = true;
		const doorToDoorPaths = this.getDoorToDoorPaths(room);
		for (const key of doorToDoorPaths) this.tilesReservedForPath.add(key);

		const pathPositions = new Set<string>();
		for (const key of doorToDoorPaths) {
			const xy = XY.fromString(key);
			const brush = room.isAtBorder(xy) ? this.get3X3Brush(xy) : this.get2X2Brush(xy);
			// const brush = this.get2X2Brush(xy);
			for (const brushXY of brush) {
				if (room.containsInside(brushXY)) {
					pathPositions.add(brushXY.toString());
				}
			}
		}

		for (const xy of room.getInnerArea()) {
			if (!pathPositions.has(xy.toString())) {
				const wall = this.p.wallSupplier();
				wall.xy = xy;
				this.board.addThing(wall);
			}
		}

		for (const key of pathPositions) {
			const xy = XY.fromString(key);
			const floor = new GrayStoneFloor1(xy);
			this.board.addThing(floor);
		}
	}

	// --- Decoration methods ---

	getPositionsAvailableToPrefabs(room: RobotBaseRoom): XY[] {
		const doorToDoorPaths = this.getDoorToDoorPaths(room);
		for (const key of doorToDoorPaths) this.tilesReservedForPath.add(key);
		return room.getInnerArea().filter((xy) => !doorToDoorPaths.has(xy.toString()));
	}

	getRandomPrefab(roomType: RoomType): Prefab {
		const wallKey = (this.p.wallSupplier().constructor as any).key as string;
		const plowRobotPrefab = () => getSingleThingPrefab(() => new PlowRobot(new XY(0, 0)), false, false);
		const pottedPlantPrefab = () => getSingleThingPrefab(() => new PottedPlant(new XY(0, 0)), true, false);
		const engineerRobotPrefab = () => getSingleThingPrefab(() => new EngineerRobot(new XY(0, 0)), false, false);

		let pool: [() => Prefab, number][];

		switch (roomType) {
			case RoomType.OFFICE:
				pool = [
					[getDesksPrefab, 1],
					[getPlantsCornerPrefab, 1],
					[getDeskPrefab, 1],
					[pottedPlantPrefab, 1],
					[getWoodenDesksPrefab, 1],
					[getShelvesPrefab, 1],
					[getMachinesWithWarningStripesPrefab, 1],
					[getComputerDesksPrefab, 1],
					[() => getPillarsPrefab(wallKey), 1],
					[() => getMetalCornerPrefab(wallKey), 1],
					[plowRobotPrefab, 1],
					[engineerRobotPrefab, 1],
				];
				break;
			case RoomType.STUDY:
				pool = [
					[getPlantsCornerPrefab, 1],
					[getDeskPrefab, 1],
					[pottedPlantPrefab, 1],
					[getWoodenDesksPrefab, 1],
					[getShelvesPrefab, 1],
					[getComputerDesksPrefab, 1],
					[getSingleComputerDeskPrefab, 1],
					[() => getMetalCornerPrefab(wallKey), 1],
					[plowRobotPrefab, 1],
				];
				break;
			case RoomType.MACHINES:
				pool = [
					[getBorderPrefab, 1],
					[() => getMetalCornerPrefab(wallKey), 1],
					[getDeskPrefab, 1],
					[getShelvesPrefab, 1],
					[getMachinesWithWarningStripesPrefab, 1],
					[getComputerDesksPrefab, 1],
					[() => getFenceBoxesPrefab(wallKey), 1],
					[() => getPillarsPrefab(wallKey), 1],
					[getBigContainerPrefab, 1],
					[getFencedLootBoxesPrefab, 1],
					[getOfflineRobots, 1],
					[getOfflineRobots2, 1],
					[plowRobotPrefab, 1],
					[engineerRobotPrefab, 1],
				];
				break;
			case RoomType.POWERPLANT1:
				pool = [
					[getBorderPrefab, 1],
					[() => getMetalCornerPrefab(wallKey), 1],
					[getMachinesWithWarningStripesPrefab, 1],
					// [getOfflineRobots, 1],
					[getOfflineRobots2, 2],
					[() => buildPrefab(offlineRobots3Data), 1],
				];
				break;

			case RoomType.POWERPLANT2:
				pool = [
					[() => getMetalCornerPrefab(wallKey), 1], // enable later
					[getBarrelsOnWallPrefab, 1],
					[getExplosiveBarrelsOnWallPrefab, 1],
					[() => buildPrefab(machinesAtBorderData), 1],
				];
				break;
			case RoomType.POWERPLANT_CONTROL_ROOM:
				pool = [
					[() => buildPrefab(machinesDoubleRowAtBorderData), 1],
					[() => buildPrefab(machinesAtBorderData), 1],
					[getMachinesWithWarningStripesPrefab, 1],
					[getComputerDesksPrefab, 1],
				];
				break;
			case RoomType.BARRELS:
				pool = [
					[getBorderPrefab, 1],
					[() => getMetalCornerPrefab(wallKey), 1],
					[() => getCornerPrisonCellPrefab(wallKey), 1],
					[getShelvesPrefab, 1],
					[() => getFenceBoxesPrefab(wallKey), 1],
					[getBarrelsOnWallPrefab, 1],
					[getExplosiveBarrelsOnWallPrefab, 1],
					[getExplosiveBarrelsPrefab, 1],
					[() => getPillarsPrefab(wallKey), 1],
					[getBigContainerPrefab, 1],
					[getFencedLootBoxesPrefab, 1],
					[getOfflineRobots, 1],
					[getOfflineRobots2, 1],
					[plowRobotPrefab, 1],
				];
				break;
			case RoomType.OTHER:
			default:
				pool = [
					[getFenceWithPlants, 1],
					[plowRobotPrefab, 1],
				];
				break;
		}

		const supplier = weightedRandom(new Map(pool), this.p.rng);
		return supplier();
	}

	private getBorderRequiredAnchors(room: RobotBaseRoom, rotation: number, prefab: Prefab): XY[] {
		const r = ((rotation % 4) + 4) % 4;
		const prefabW = prefab.simpleBoard.getWidth();
		const prefabH = prefab.simpleBoard.getHeight();
		const inner = room.getInnerArea();
		switch (r) {
			case 0:
				return inner.filter((xy) => xy.y === room.minXY.y + 1);
			case 1:
				return inner.filter((xy) => xy.x === room.minXY.x + 1);
			case 2:
				return inner.filter((xy) => xy.y === room.maxXY.y - prefabH);
			case 3:
				return inner.filter((xy) => xy.x === room.maxXY.x - prefabW);
		}
		return [];
	}

	private getCornerRequiredAnchors(room: RobotBaseRoom, rotation: number, prefab: Prefab): XY[] {
		const r = ((rotation % 4) + 4) % 4;
		const prefabW = prefab.simpleBoard.getWidth();
		const prefabH = prefab.simpleBoard.getHeight();
		switch (r) {
			case 0:
				return [new XY(room.minXY.x + 1, room.minXY.y + 1)];
			case 1:
				return [new XY(room.minXY.x + 1, room.maxXY.y - prefabH)];
			case 2:
				return [new XY(room.maxXY.x - prefabW, room.maxXY.y - prefabH)];
			case 3:
				return [new XY(room.maxXY.x - prefabW, room.minXY.y + 1)];
		}
		return [];
	}

	decorateArea(room: RobotBaseRoom): void {
		if (room.type == null) {
			room.type = randomElement(this.p.availableRoomTypes, this.p.rng) ?? RoomType.OTHER;
		}

		const availablePositions = this.getPositionsAvailableToPrefabs(room).filter(
			(xy) => !this.reservedPrefabTiles.has(xy.toString()),
		);
		const availableSet = new Set(availablePositions.map((xy) => `${xy.x},${xy.y}`));
		const availableXYs = [...availablePositions];

		const minDecorated = Math.floor(this.p.requiredDecorationPercentMin * availablePositions.length);
		const maxDecorated = Math.floor(this.p.requiredDecorationPercentMax * availablePositions.length);

		let tilesDecorated = 0;
		let attempts = 0;

		while (tilesDecorated < minDecorated && attempts < 200) {
			attempts++;
			const prefab = this.getRandomPrefab(room.type!);

			const prefabTileCount = prefab.simpleBoard.getNumberOfTilesUsed();

			if (prefabTileCount + tilesDecorated > maxDecorated) continue;

			const randomRotation = randomInt(0, 3, this.p.rng);
			prefab.rotate(randomRotation);

			let candidatePositions = availableXYs;

			if (prefab.cornerRequired) {
				const anchors = this.getCornerRequiredAnchors(room, randomRotation, prefab);
				candidatePositions = candidatePositions.filter((xy) => anchors.some((a) => a.equals(xy)));
			}
			if (prefab.borderRequired) {
				const anchors = this.getBorderRequiredAnchors(room, randomRotation, prefab);
				candidatePositions = candidatePositions.filter((xy) => anchors.some((a) => a.equals(xy)));
			}

			const usedPositions = putPrefabOnArea(this.board, availableXYs, prefab, this.p.rng, candidatePositions);

			if (usedPositions == null) continue;

			for (const pos of usedPositions) {
				const key = `${pos.x},${pos.y}`;
				availableSet.delete(key);
				const idx = availableXYs.findIndex((xy) => xy.x === pos.x && xy.y === pos.y);
				if (idx !== -1) availableXYs.splice(idx, 1);
			}
			tilesDecorated += usedPositions.length;
		}
	}

	placeRequiredPrefabs(
		normalRooms: Set<RobotBaseRoom>,
		roomsWithRequiredPrefab: Set<RobotBaseRoom>,
		roomsDisallowedAsStarter: Set<RobotBaseRoom>,
	): void {
		for (const req of this.p.requiredPrefabs) {
			const canBePlacedInVaultRoom = req.canBePlacedInVaultRoom ?? false;
			const canBePlacedInStarterRoom = req.canBePlacedInStarterRoom ?? true;
			const allowedRoomTypes = req.allowedRoomTypes;

			const candidateRooms: RobotBaseRoom[] = [];
			for (const room of normalRooms) {
				if (allowedRoomTypes && !allowedRoomTypes.includes(room.type!)) continue;
				candidateRooms.push(room);
			}
			if (canBePlacedInVaultRoom) {
				for (const room of this.vaultRooms) {
					if (allowedRoomTypes && !allowedRoomTypes.includes(room.type!)) continue;
					candidateRooms.push(room);
				}
			}

			const areaByRoom = new Map<RobotBaseRoom, XY[]>();
			const area: XY[] = [];
			const roomByKey = new Map<string, RobotBaseRoom>();
			for (const room of candidateRooms) {
				const roomArea = this.getPositionsAvailableToPrefabs(room).filter(
					(xy) => !this.reservedPrefabTiles.has(xy.toString()),
				);
				areaByRoom.set(room, roomArea);
				for (const xy of roomArea) {
					area.push(xy);
					roomByKey.set(xy.toString(), room);
				}
			}

			const prefab = req.prefab.clone();
			const rotation = randomInt(0, 3, this.p.rng);
			prefab.rotate(rotation);

			let allowedPositions: XY[] | undefined = undefined;
			if (prefab.cornerRequired || prefab.borderRequired) {
				allowedPositions = [];
				for (const [room, roomArea] of areaByRoom) {
					let filtered = roomArea;
					if (prefab.cornerRequired) {
						const anchors = this.getCornerRequiredAnchors(room, rotation, prefab);
						filtered = filtered.filter((xy) => anchors.some((a) => a.equals(xy)));
					}
					if (prefab.borderRequired) {
						const anchors = this.getBorderRequiredAnchors(room, rotation, prefab);
						filtered = filtered.filter((xy) => anchors.some((a) => a.equals(xy)));
					}
					allowedPositions.push(...filtered);
				}
			}

			const placedXYs = putPrefabOnArea(this.board, area, prefab, this.p.rng, allowedPositions);
			if (!placedXYs) {
				throw new LevelGeneratorScrewUp("Could not place required prefab");
			}

			for (const xy of placedXYs) {
				this.reservedPrefabTiles.add(xy.toString());
			}
			for (const thing of prefab.simpleBoard.getThingList()) {
				this.protectedThings.add(thing);
			}
			for (const thing of prefab.simpleBoard.getNonPhysicalThings()) {
				this.protectedThings.add(thing);
			}

			const placedRoom = roomByKey.get(placedXYs[0].toString());
			if (placedRoom) {
				roomsWithRequiredPrefab.add(placedRoom);
				if (!canBePlacedInStarterRoom) {
					roomsDisallowedAsStarter.add(placedRoom);
				}
			}
		}
	}

	decorateRoom(room: RobotBaseRoom, skipCorridor: boolean = false): void {
		if (
			!skipCorridor &&
			this.getRoomDoors(room).length > 1 &&
			randomChance(this.p.corridorRoomProbability, this.p.rng)
		) {
			this.fillRoomWithCorridors(room);
			return;
		}
		this.decorateArea(room);
	}

	// --- Population methods ---

	getRandomPlayerAccessibleXY(extraFilter?: (xy: XY) => boolean): XY | null {
		const playerXY = this.generationPlayerXY!;

		const accessible = breadthFirstSearch(
			playerXY,
			(xy) => xy.isInside(this.board) && (this.board.isPassable(xy) || this.isDoorTile(xy)),
		);

		// Shuffle for randomness
		for (let i = accessible.length - 1; i > 0; i--) {
			const j = Math.floor(this.p.rng() * (i + 1));
			[accessible[i], accessible[j]] = [accessible[j], accessible[i]];
		}

		for (const xy of accessible) {
			if (playerXY.distanceTo(xy) <= 8) continue;
			if (this.isDoorTile(xy) || this.hasAdjacentDoor(xy)) continue;
			const room = this.findRoom(xy);
			if (room && this.vaultRooms.has(room)) continue;
			if (extraFilter && !extraFilter(xy)) continue;
			return xy;
		}
		return null;
	}

	placeMonsterSpawners(count: number): void {
		for (let i = 0; i < count; i++) {
			const xy = this.getRandomPlayerAccessibleXY((xy) => !this.tilesReservedForPath.has(xy.toString()));
			if (xy == null) continue;

			const config = new MobSpawnerConfig();
			config.addEntry({ mobType: PlowRobot.key, share: 1 });
			config.addEntry({ mobType: FloatingRobot.key, share: 1 });
			config.addEntry({ mobType: Robot.key, share: 1 });

			const spawner = new RobotSpawner(xy, true, config);
			this.board.addThing(spawner);
		}
	}

	pickExitXY(): XY {
		const playerXY = this.generationPlayerXY!;

		const isOpenableDoor = (xy: XY): boolean =>
			this.board.getThingOfClass(WoodenDoor, xy) !== null ||
			this.board.getThingOfClass(BlastDoor, xy) !== null ||
			this.board.getThingOfClass(SlidingDoor, xy) !== null ||
			this.board.getThingOfClass(MetalDoor, xy) !== null;

		const doorPathMap = DistanceMap.buildFrom(this.board, playerXY, (xy) => {
			if (!this.board.isInBounds(xy)) return false;
			return this.board.isPassable(xy) || isOpenableDoor(xy);
		});

		const destroyPathMap = DistanceMap.buildFrom(this.board, playerXY, (xy) => {
			if (!this.board.isInBounds(xy)) return false;
			if (this.board.isPassable(xy) || isOpenableDoor(xy)) return true;
			return this.board.getThingsAt(xy).some((t) => t instanceof DestroyableObstacle);
		});

		const minDigDistance = Math.max(Math.floor(this.p.minExitDistance / 5), 5);
		const candidates: XY[] = [];
		doorPathMap.forEach((xy, distance) => {
			if (distance < this.p.minExitDistance) return;
			if (!this.board.isPassable(xy)) return;
			if (this.board.hasMob(xy)) return;
			if (this.isDoorTile(xy) || this.hasAdjacentDoor(xy)) return;
			const digDistance = destroyPathMap.get(xy);
			if (digDistance === undefined || digDistance < minDigDistance) return;
			const room = this.findRoom(xy);
			if (room && this.vaultRooms.has(room)) return;
			candidates.push(xy);
		});

		const exitXY = randomElement(candidates, this.p.rng);
		if (!exitXY) {
			throw new LevelGeneratorScrewUp("Exit could not be placed far enough");
		}
		return exitXY;
	}

	placeLevelExit(exitXY: XY, destinationLevel: number): void {
		this.board.addThing(this.p.exitSupplier(exitXY, destinationLevel));

		if (!randomChance(this.p.lockedExitProbability, this.p.rng)) return;

		const room = this.findRoom(exitXY);
		if (!room) return;

		// BFS from exitXY to find connected inner area within the room.
		// Check for wall classes specifically (not isPassable) so decorations like tables don't fragment the area.
		const wallClass = this.p.wallSupplier().constructor as new (...args: any[]) => PhysicalThing;
		const isWallTile = (xy: XY) =>
			this.board.getThingOfClass(wallClass, xy) !== null ||
			this.board.getThingOfClass(MetalBlock, xy) !== null ||
			this.board.getThingOfClass(WoodenDoor, xy) !== null;
		const innerAreaList = breadthFirstSearch(
			exitXY,
			(xy) => xy.isInside(this.board) && !isWallTile(xy) && !this.isDoorTile(xy) && room.contains(xy),
		);
		const innerArea = new Set(innerAreaList.map((xy) => xy.toString()));

		// Compute border tiles: all 8 neighbors (including diagonals) not in inner area
		const borders = new Set<string>();
		for (const xy of innerAreaList) {
			for (const neighbor of xy.getNeighbors()) {
				if (!neighbor.isInside(this.board)) continue;
				if (innerArea.has(neighbor.toString())) continue;
				borders.add(neighbor.toString());
			}
		}

		// Seal borders: clear everything then place blast doors or metal blocks.
		// Remove sealed door positions from doorTiles so the BFS in
		// getRandomPlayerAccessibleXY won't treat blast doors as passable.
		for (const key of borders) {
			const xy = XY.fromString(key);
			removeAllThingsAt(this.board, xy);
			if (this.isDoorTile(xy)) {
				this.doorTiles.delete(key);
				this.board.addThing(new BlastDoor(xy, EXIT_ROOM_DOOR_ID));
			} else {
				this.board.addThing(new MetalBlock(xy));
			}
		}

		// Place terminal to open blast doors
		const terminalXY = this.getRandomPlayerAccessibleXY();

		if (terminalXY == null) {
			throw new LevelGeneratorScrewUp("Nowhere to place terminal.");
		}
		this.board.addThing(new OpenBlastDoorsTerminal(terminalXY, EXIT_ROOM_DOOR_ID));
	}

	doPlaceEquation(): void {
		const playerXY = this.generationPlayerXY!;
		const playerRoom = this.findRoom(playerXY);

		const equationXY = this.getRandomPlayerAccessibleXY((xy) => {
			const up = xy.addDirection(Direction.N);
			if (!up.isInside(this.board)) return false;
			if (this.board.isPassable(up)) return false;

			const room = this.findRoom(xy);
			if (!room) return false;
			if (room.corridor) return false;
			if (room.type !== RoomType.STUDY) return false;
			if (room === playerRoom) return false;

			if (!this.isClear(xy)) return false;
			if (!room.isAtBorder(xy)) return false;
			if (!this.isClear(xy.add(-1, 0))) return false;
			if (!this.isClear(xy.add(1, 0))) return false;
			if (!this.isClear(xy.add(2, 0))) return false;

			// Row below (south = y+1 in screen coords)
			if (!this.isClear(xy.add(0, 1))) return false;
			if (!this.isClear(xy.add(-1, 1))) return false;
			if (!this.isClear(xy.add(1, 1))) return false;
			if (!this.isClear(xy.add(2, 1))) return false;

			return true;
		});

		if (!equationXY) throw new LevelGeneratorScrewUp("Can't find a place for the equation");

		this.board.addThing(new Equation(equationXY));
		this.board.addThing(new InvisibleWall(equationXY));
		this.board.addThing(new InvisibleWall(equationXY.addDirection(Direction.E)));
	}

	putRoom(room: RobotBaseRoom, supplier: () => PhysicalThing = this.p.wallSupplier): void {
		// Top border
		for (let x = room.minXY.x; x <= room.maxXY.x; x++) {
			this.putWall(supplier(), new XY(x, room.maxXY.y));
		}
		// Bottom border
		for (let x = room.minXY.x; x <= room.maxXY.x; x++) {
			this.putWall(supplier(), new XY(x, room.minXY.y));
		}
		// Left border
		for (let y = room.minXY.y + 1; y <= room.maxXY.y - 1; y++) {
			this.putWall(supplier(), new XY(room.minXY.x, y));
		}
		// Right border
		for (let y = room.minXY.y + 1; y <= room.maxXY.y - 1; y++) {
			this.putWall(supplier(), new XY(room.maxXY.x, y));
		}
	}

	runGeneration(): Board {
		this.board = new Board(BOARD_WIDTH, BOARD_HEIGHT, this.p.rng);
		this.board.defaultFloor = "GrayStoneFloor2";
		this.board.areaName = Strings.get("area_robot_base") ?? "Robot Base";

		// BSP partitioning
		const mainRoom = new RobotBaseRoom(
			new XY(this.p.margin, this.p.margin),
			new XY(BOARD_WIDTH - 1 - this.p.margin, BOARD_HEIGHT - 1 - this.p.margin),
		);
		this.partitionRoom(mainRoom);

		// Classify rooms: vault vs normal
		const normalRooms: Set<RobotBaseRoom> = new Set();
		for (const room of this.rooms) {
			if (this.getRoomDoors(room).length <= 1 && randomChance(this.p.vaultRoomProbability, this.p.rng)) {
				this.vaultRooms.add(room);
			} else {
				normalRooms.add(room);
			}
		}

		// Vault rooms: MetalBlock walls, GraySlab floor
		for (const room of this.vaultRooms) {
			this.putRoom(room, () => new MetalBlock(new XY(0, 0)));
			this.fillRoom(room, () => new GraySlab(new XY(0, 0)));
			room.type = randomElement(this.p.availableRoomTypes, this.p.rng) ?? RoomType.OTHER;
		}

		// Normal rooms: default walls, assign random room type
		for (const room of normalRooms) {
			this.putRoom(room);
			room.type = randomElement(this.p.availableRoomTypes, this.p.rng) ?? RoomType.OTHER;
		}

		// Place required prefabs before decoration so reserved rooms aren't turned into corridors
		const roomsWithRequiredPrefab = new Set<RobotBaseRoom>();
		const roomsDisallowedAsStarter = new Set<RobotBaseRoom>();
		this.placeRequiredPrefabs(normalRooms, roomsWithRequiredPrefab, roomsDisallowedAsStarter);

		// Decorate all rooms
		for (const room of this.vaultRooms) {
			this.decorateRoom(room, roomsWithRequiredPrefab.has(room));
		}
		for (const room of normalRooms) {
			this.decorateRoom(room, roomsWithRequiredPrefab.has(room));
		}

		// Player placement: pick from tilesReservedForPath, not adjacent to door, not in vault
		const pathTiles = [...this.tilesReservedForPath]
			.map((key) => XY.fromString(key))
			.filter((xy) => !this.hasAdjacentDoor(xy))
			.filter((xy) => this.board.isPassable(xy))
			.filter((xy) => {
				const room = this.findRoom(xy);
				if (!room) return true;
				if (this.vaultRooms.has(room)) return false;
				if (roomsDisallowedAsStarter.has(room)) return false;
				return true;
			});

		const playerXY = randomElement(pathTiles, this.p.rng);
		if (!playerXY) {
			throw new LevelGeneratorScrewUp("Cannot place player");
		}
		this.generationPlayerXY = playerXY;
		this.board.addThing(this.p.entranceSupplier(playerXY, this.p.previousLevelNumber ?? this.p.levelNumber - 1));

		// Remove mobs from the player's starting room
		const playerRoom = this.findRoom(playerXY);
		if (playerRoom) {
			for (const xy of playerRoom.getInnerArea()) {
				for (const thing of this.board.getThingsAt(xy)) {
					if (thing instanceof Mob) {
						this.board.removeThing(thing);
					}
				}
			}
		}

		// Cap excessive things
		removeExcessiveThings(
			this.board,
			this.p.maxMobs,
			(t: Thing): t is Mob => t instanceof Mob,
			this.p.rng,
			this.protectedThings,
		);
		removeExcessiveThings(
			this.board,
			this.p.maxLootBoxes,
			(t: Thing): t is LootBox => t instanceof LootBox,
			this.p.rng,
			this.protectedThings,
		);

		// Populate loot boxes
		populateLootBoxes(this.board, this.p.lootTable, this.p.metalLootTable, this.p.rewardLevel, this.p.rng);

		// Populate any vending machines placed via required prefabs
		if (this.p.vendingTable) {
			populateVendingMachines(this.board, this.p.vendingTable, this.p.rng);
		}

		// Monster spawners
		this.placeMonsterSpawners(this.p.numberOfSpawners);

		// Equation
		if (this.p.placeEquation) {
			this.doPlaceEquation();
		}

		// Level exit
		if (this.p.placeExit) {
			const exitXY = this.pickExitXY();
			this.placeLevelExit(exitXY, this.p.exitLevelNumber ?? this.p.levelNumber + 1);
		}

		// fills the outside with walls
		breadthFirstSearch(new XY(0, 0), (xy) => this.board.isPassable(xy)).forEach((xy) =>
			this.putWall(this.p.wallSupplier(), xy),
		);
		assignGoldToPrePlacedMobs(this.board);
		this.board.dynamicGoldBudget = 250;

		return this.board;
	}
}
